import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader, Card } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Download, Upload, FileDown, Trash2, ShieldCheck, UserCog, Edit } from "lucide-react";
import { STATES } from "@/lib/states";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminUsers() {
  const { user } = useAuth();
  const { t } = useLang();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [importResult, setImportResult] = useState(null);
  
  const [editingUser, setEditingUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (roleFilter) params.role = roleFilter;
    api.get("/admin/users", { params }).then((r) => setUsers(r.data));
  }, [search, roleFilter]);

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);

  const download = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const doImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/import/users", fd);
      setImportResult(data);
      toast.success(`${data.created} ${t("created")}`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    e.target.value = "";
  };

  const del = async (id) => {
    try { await api.delete(`/admin/users/${id}`); toast.success(t("delete")); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setRole = async (id, role) => {
    try { await api.put(`/admin/users/${id}/role`, { role }); toast.success(t("save")); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  
  const openEditModal = (u) => {
    setEditingUser({ ...u, password: "" });
    setIsEditModalOpen(true);
  };

  const handleEditSave = async () => {
    try {
      await api.put(`/admin/users/${editingUser._id || editingUser.id}`, editingUser);
      toast.success("User updated");
      setIsEditModalOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader title={t("users")} subtitle="Manage trainees & admins"
        action={
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => download("/admin/import/template", "user_import_template.csv")} data-testid="download-template"><FileDown className="h-4 w-4 mr-1.5" />{t("downloadTemplate")}</Button>
            <Button variant="outline" size="sm" onClick={() => download("/admin/export/users", "users.csv")} data-testid="export-users"><Download className="h-4 w-4 mr-1.5" />{t("export")}</Button>
            <label>
              <Button variant="outline" size="sm" asChild data-testid="import-users"><span className="cursor-pointer"><Upload className="h-4 w-4 mr-1.5" />{t("importUsers")}</span></Button>
              <input type="file" accept=".csv" className="hidden" onChange={doImport} />
            </label>
          </div>
        } />

      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`${t("search")} badge / name`} className="pl-9" data-testid="user-search" />
        </div>
        <div className="flex gap-1 border border-slate-200 rounded-md p-1 bg-white">
          {["", "trainee", "admin"].map((r) => (
            <button key={r} onClick={() => setRoleFilter(r)} data-testid={`filter-${r || "all"}`}
              className={`px-3 py-1.5 text-sm rounded font-medium capitalize ${roleFilter === r ? "bg-[#0F172A] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              {r || "All"}
            </button>
          ))}
        </div>
      </div>

      {importResult && (
        <Card className="mb-4 border-blue-200 bg-blue-50/50" data-testid="import-result">
          <div className="font-medium text-slate-900 mb-2">{t("importResult")}: {importResult.created} {t("created")} / {importResult.total_rows} rows</div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {importResult.results.filter((r) => r.status === "error").map((r) => (
              <div key={r.row} className="text-xs text-red-700">Row {r.row} ({r.badge_number}): {r.errors.join(", ")}</div>
            ))}
            {importResult.results.every((r) => r.status === "created") && <div className="text-xs text-emerald-700">All rows imported successfully.</div>}
          </div>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>{t("badgeNumber")}</TableHead>
              <TableHead>{t("name")}</TableHead>
              <TableHead>{t("designation")}</TableHead>
              <TableHead>{t("state")}</TableHead>
              <TableHead>{t("role")}</TableHead>
              <TableHead className="text-right">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.badge_number}`}>
                <TableCell className="font-mono text-sm">{u.badge_number}</TableCell>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-slate-500">{u.designation}</TableCell>
                <TableCell className="text-slate-500">{u.state}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium border ${u.role === "admin" ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                    {u.role === "admin" ? <ShieldCheck className="h-3 w-3" /> : <UserCog className="h-3 w-3" />}{u.role}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEditModal(u)} data-testid={`edit-${u.badge_number}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    {u.id !== user.id && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-${u.badge_number}`}><Trash2 className="h-4 w-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t("delete")} {u.badge_number}?</AlertDialogTitle>
                            <AlertDialogDescription>This will remove the user and their training sessions.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t("back")}</AlertDialogCancel>
                            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => del(u.id)} data-testid={`confirm-delete-${u.badge_number}`}>{t("delete")}</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!users.length && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">{t("noData")}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User {editingUser?.badge_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name</Label>
              <Input value={editingUser?.name || ""} onChange={e => setEditingUser({...editingUser, name: e.target.value})} />
            </div>
            <div>
              <Label>Designation</Label>
              <Input value={editingUser?.designation || ""} onChange={e => setEditingUser({...editingUser, designation: e.target.value})} />
            </div>
            <div>
              <Label>State</Label>
              <select value={editingUser?.state || ""} onChange={e => setEditingUser({...editingUser, state: e.target.value})} className="mt-1.5 w-full h-10 rounded-md border border-slate-300 px-3 text-sm">
                <option value="">Select State</option>
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label>Role</Label>
              <select value={editingUser?.role || "trainee"} onChange={e => setEditingUser({...editingUser, role: e.target.value})} className="mt-1.5 w-full h-10 rounded-md border border-slate-300 px-3 text-sm">
                <option value="trainee">Trainee</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <Label>New Password (Optional)</Label>
              <Input type="password" placeholder="Leave blank to keep unchanged" value={editingUser?.password || ""} onChange={e => setEditingUser({...editingUser, password: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
            <Button onClick={handleEditSave}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
